if (__LOG_INFO__) AppLog.info('2. Global State script loading...');
window.AppState = window.AppState || {};

// Synchronous initial state setup
(function setInitialState() {
    const isMobile = navigator.userAgent.match(/Android|iPhone|iPad|iPod/i) ? true : false;
    const hasVRDisplays = 'getVRDisplays' in navigator || 'xr' in navigator;
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isVRDevice = isMobile || userAgent.includes('vr') || userAgent.includes('oculus') || userAgent.includes('cardboard');
    const isDesktopBrowser = !isVRDevice && (
        userAgent.includes('chrome') ||
        userAgent.includes('safari') ||
        userAgent.includes('firefox') ||
        userAgent.includes('edg') ||
        userAgent.includes('opr')
    ) && !isIOS;

    AppState.isTrueVRMode = isVRDevice && hasVRDisplays;
    AppState.isPseudoVRMode = false;
    AppState.isDesktopMode = isDesktopBrowser && !AppState.isTrueVRMode;
    AppState.isFullscreen = !!document.fullscreenElement ||
        !!document.webkitFullscreenElement ||
        !!document.mozFullScreenElement ||
        !!document.msFullscreenElement;
    AppState.isMobileDevice = isMobile;

    // Screen detection logic
    // Aspect ratio calculation functions
    function getAspectRatio(width, height) {
        // Calculate the greatest common divisor (GCD) using Euclidean algorithm
        function gcd(a, b) {
            return b === 0 ? a : gcd(b, a % b);
        }

        // Find GCD of width and height
        const divisor = gcd(width, height);

        // Simplify the ratio
        const ratioWidth = width / divisor;
        const ratioHeight = height / divisor;

        // Return the aspect ratio as a string
        return `${ratioWidth}:${ratioHeight}`;
    }

    function getApproximateAspectRatio(width, height) {
        const ratio = width / height;
        const commonRatios = [
            { name: "4:3", value: 4 / 3, threshold: 0.05 },
            { name: "5:4", value: 5 / 4, threshold: 0.05 },
            { name: "16:9", value: 16 / 9, threshold: 0.05 },
            { name: "16:10", value: 16 / 10, threshold: 0.05 },
            { name: "21:9", value: 21 / 9, threshold: 0.1 },
            { name: "32:9", value: 32 / 9, threshold: 0.1 },
            { name: "3:2", value: 3 / 2, threshold: 0.05 },
        ];

        // Find the closest common ratio
        for (const r of commonRatios) {
            if (Math.abs(ratio - r.value) < r.threshold) {
                return r.name;
            }
        }

        // Fallback to exact ratio
        return getAspectRatio(width, height);
    }

    // Detect screen parameters
    const screenWidth = window.screen.width || 1920; // Fallback to 1920x1080
    const screenHeight = window.screen.height || 1080;
    const pixelRatio = window.devicePixelRatio || 1;
    const screenRatio = getApproximateAspectRatio(screenWidth, screenHeight);

    // Store screenRatio in AppState
    AppState.screenRatio = screenRatio;

    // Store screen parameters using setCacheItem
    setCacheItem('screenWidth', screenWidth, 86400);
    setCacheItem('screenHeight', screenHeight, 86400);
    setCacheItem('devicePixelRatio', pixelRatio, 86400);
    setCacheItem('screenRatio', screenRatio, 86400);

    if (__LOG_INFO__) AppLog.info('Initial Synchronous App State:', AppState);
    if (__LOG_INFO__) AppLog.info('Screen Parameters:', {
        screenWidth,
        screenHeight,
        devicePixelRatio: pixelRatio,
        screenRatio
    });

    setCacheItem('data-interval', '1D', 24 * 60);
})();

function initializeAppState() {
    if (!window.AFRAME) {
        if (__LOG_ERROR__) AppLog.error('A-Frame is not loaded. Global state cannot be initialized further.');
        return;
    }

    const scene = document.querySelector('a-scene');

    // Function to get URL parameter
    function getUrlParameter(name) {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(name);
    }

    // Function to load appropriate HTML file (only from index.html)
    function loadEnvironment() {
        const currentPath = window.location.pathname.split('/').pop();
        if (currentPath !== 'index.html') {
            return; // Skip redirect if not on index.html
        }

        let targetUrl;
        if (AppState.isTrueVRMode) {
            targetUrl = 'vr.html';
        } else if (AppState.isPseudoVRMode) {
            targetUrl = 'desktop.html';
        } else if (AppState.isDesktopMode) {
            targetUrl = 'desktop.html';
        } else {
            targetUrl = 'desktop.html';
            if (__LOG_WARN__) AppLog.warn('No clear AppState detected, defaulting to desktop_single.html');
        }
        if (__LOG_INFO__) AppLog.info("Switching page to: ", targetUrl);

        const passcode = getUrlParameter('passcode');
        if (passcode) {
            targetUrl += `?passcode=${encodeURIComponent(passcode)}`;
        }

        if (currentPath !== targetUrl.split('?')[0]) {
            window.location.href = targetUrl;
        }
    }

    // Function to check if WebXR session is active
    async function isXRSessionActive() {
        if (navigator.xr) {
            try {
                const session = await navigator.xr.isSessionSupported('immersive-vr');
                return session; // Returns true if immersive-vr is supported and likely active
            } catch (error) {
                if (__LOG_ERROR__) AppLog.error('XR session check failed:', error);
                return false;
            }
        }
        return false;
    }

    // Function to update VR state and emit event only on VR mode change
    async function updateVRState() {
        if (!scene) return;

        const inVRMode = scene.is('vr-mode');
        const isMobile = AFRAME.utils.device.isMobile();
        const hasXR = 'xr' in navigator;
        const xrSessionActive = await isXRSessionActive();
        const userAgent = navigator.userAgent.toLowerCase();
        const isIOS = /iphone|ipad|ipod/.test(userAgent);
        const isVRDevice = isMobile || userAgent.includes('vr') || userAgent.includes('oculus') || userAgent.includes('cardboard') || userAgent.includes('quest');
        const isDesktopBrowser = !isVRDevice && (
            userAgent.includes('chrome') ||
            userAgent.includes('safari') ||
            userAgent.includes('firefox') ||
            userAgent.includes('edg') ||
            userAgent.includes('opr')
        ) && !isIOS;

        // Store previous VR state to detect changes
        const prevTrueVRMode = AppState.isTrueVRMode;
        const prevPseudoVRMode = AppState.isPseudoVRMode;
        const prevDesktopMode = AppState.isDesktopMode;

        if (inVRMode || xrSessionActive) {
            AppState.isTrueVRMode = hasXR && (isVRDevice || xrSessionActive);
            AppState.isPseudoVRMode = isDesktopBrowser && !AppState.isTrueVRMode;
            AppState.isDesktopMode = false;
        } else {
            AppState.isTrueVRMode = false;
            AppState.isPseudoVRMode = false;
            AppState.isDesktopMode = isDesktopBrowser || (!isVRDevice && !isIOS);
        }

        if (__LOG_INFO__) AppLog.info('VR State Updated:', AppState, 'In VR Mode:', inVRMode, 'Has XR:', hasXR, 'XR Session Active:', xrSessionActive, 'User Agent:', userAgent);

        // Emit event only if VR mode changes
        if (prevTrueVRMode !== AppState.isTrueVRMode ||
            prevPseudoVRMode !== AppState.isPseudoVRMode ||
            prevDesktopMode !== AppState.isDesktopMode) {
            if (scene) {
                scene.emit('app-state-changed', { state: AppState });
            }
        }
    }

    // Function to update fullscreen state (no event emission unless VR mode changes)
    function updateFullscreenState() {
        AppState.isFullscreen = !!document.fullscreenElement ||
            !!document.webkitFullscreenElement ||
            !!document.mozFullScreenElement ||
            !!document.msFullscreenElement;
        if (__LOG_INFO__) AppLog.info('Fullscreen State Updated:', AppState);
        // No event emission here; VR mode changes are handled by updateVRState
    }

    // Initial dynamic setup
    if (scene) {
        updateVRState();
    } else {
        loadEnvironment(); // Initial redirect from index.html
    }

    // Event listeners for dynamic updates
    if (scene) {
        scene.addEventListener('enter-vr', updateVRState);
        scene.addEventListener('exit-vr', updateVRState);
    }

    document.addEventListener('fullscreenchange', updateFullscreenState);
    document.addEventListener('webkitfullscreenchange', updateFullscreenState);
    document.addEventListener('mozfullscreenchange', updateFullscreenState);
    document.addEventListener('MSFullscreenChange', updateFullscreenState);

    // Add listener for scene load if it doesn't exist yet
    if (!scene) {
        document.addEventListener('loaded', () => {
            const newScene = document.querySelector('a-scene');
            if (newScene) {
                newScene.addEventListener('enter-vr', updateVRState);
                newScene.addEventListener('exit-vr', updateVRState);
                updateVRState();
            }
        }, { once: true });
    }
}

// Run initialization after A-Frame loads
document.addEventListener('DOMContentLoaded', () => {
    if (window.AFRAME) {
        initializeAppState();
    } else {
        const aframeScript = document.querySelector('script[src*="/aframe/aframe.min.js"]');
        if (aframeScript) {
            aframeScript.addEventListener('load', initializeAppState, { once: true });
        }
    }
});

if (typeof module !== 'undefined' && module.exports) {
    module.exports = AppState;
}